import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Download, Loader2, Library, PackageOpen, FileText, Smartphone, KeyRound } from "lucide-react";
import { fetchMisCompras, descargarProducto, descargarLicencia } from "../lib/software";
import { humanizeAxiosError } from "../lib/api";
import { toast } from "sonner";
import EstadoHerramientas from "../components/EstadoHerramientas";

function fmtFecha(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" });
}

export default function MisDescargas() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    fetchMisCompras().then((d) => setItems(d.items || [])).catch(() => setItems([]));
  }, []);

  async function handleDescargar(it) {
    setBusy(it.id);
    try {
      await descargarProducto(it.id);
      toast.success("Generando tu descarga segura…");
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setBusy(null);
    }
  }

  async function handleLicencia(it) {
    setBusy(it.id + "-lic");
    try {
      await descargarLicencia(it.id);
      toast.success("Descargando archivo de licencia…");
    } catch (e) {
      toast.error(humanizeAxiosError(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="px-5 md:px-8 py-8" data-testid="mis-descargas-page">
      <Link to="/software" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800">
        <ChevronLeft size={16} /> Volver a ConstruLink Software
      </Link>

      <EstadoHerramientas />

      <div className="mt-4 flex items-center gap-3">
        <span className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
          <Library size={22} className="text-emerald-600" />
        </span>
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Mis Descargas</h1>
          <p className="text-sm text-slate-500">Productos digitales que has adquirido. Descárgalos las veces que necesites.</p>
        </div>
      </div>

      {items === null ? (
        <div className="mt-10 flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando tu biblioteca…</div>
      ) : items.length === 0 ? (
        <div className="mt-10 bg-white rounded-2xl border border-slate-100 shadow-soft p-10 text-center" data-testid="mis-descargas-vacio">
          <PackageOpen size={40} className="mx-auto text-slate-300" />
          <h3 className="mt-4 text-lg font-bold text-slate-900">Aún no tienes descargas</h3>
          <p className="mt-1 text-sm text-slate-500">Explora la tienda de plantillas y herramientas del Pilar Software.</p>
          <Link to="/software" className="mt-6 inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-blue text-white font-semibold px-5 py-2.5 rounded-xl transition-colors" data-testid="mis-descargas-explorar">
            Ir a la tienda
          </Link>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          {items.map((it) => (
            <div key={it.id} className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5 flex flex-col" data-testid={`descarga-item-${it.id}`}>
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                  {it.tipo_producto === "programa" ? <Smartphone size={18} /> : <FileText size={18} />}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-slate-100 text-slate-600">{it.categoria}</span>
                {it.version && <span className="text-[10px] font-semibold text-slate-400">v{it.version}</span>}
              </div>
              <h3 className="mt-3 text-lg font-bold text-slate-900">{it.nombre}</h3>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed flex-1">{it.descripcion}</p>
              <div className="mt-4 flex items-center justify-between gap-3">
                <span className="text-xs text-slate-400">Adquirido el {fmtFecha(it.comprado_el)}</span>
                <div className="flex items-center gap-2">
                  {it.tiene_licencia && (
                    <button
                      onClick={() => handleLicencia(it)}
                      disabled={busy === it.id + "-lic"}
                      data-testid={`licencia-btn-${it.id}`}
                      className="inline-flex items-center gap-1.5 border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold text-sm px-3 py-2 rounded-xl transition-colors disabled:opacity-60"
                    >
                      {busy === it.id + "-lic" ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
                      Licencia
                    </button>
                  )}
                  <button
                    onClick={() => handleDescargar(it)}
                    disabled={busy === it.id}
                    data-testid={`descarga-btn-${it.id}`}
                    className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm px-4 py-2 rounded-xl transition-colors disabled:opacity-60"
                  >
                    {busy === it.id ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                    Descargar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
